// Extra space (px) reserved around a word's text: the chip's own padding
// plus a visible gap to the next chip, scaled by the spread setting.
const chipReserve = (fontSize, paddingScale) => (Math.ceil(fontSize * 0.16) + 6) * paddingScale;

// d3-cloud's default collision uses glyph outlines, letting small words nest
// inside big letterforms' concave gaps - fine for bare text, but for the
// 'chips' style it puts small chips visually on top of big ones. This canvas
// makes d3-cloud collide against solid chip-sized rectangles instead.
export const rectSpriteCanvas = (paddingScale = 1) => {
  const canvasEl = document.createElement('canvas');
  const originalGetContext = canvasEl.getContext.bind(canvasEl);
  canvasEl.getContext = (type, opts) => {
    const ctx = originalGetContext(type, opts);
    if (type === '2d' && ctx && !ctx.__chipPatched) {
      ctx.__chipPatched = true;
      const originalMeasureText = ctx.measureText.bind(ctx);
      const currentFontSize = () => parseFloat((ctx.font.match(/(\d+(?:\.\d+)?)px/) || [])[1]) || 12;
      ctx.measureText = (text) => {
        const measured = originalMeasureText(text);
        return { width: measured.width + chipReserve(currentFontSize(), paddingScale) * 2 };
      };
      // Draw the rect symmetric around y=0 (baseline) to match the rendered
      // chip, which is flex-centered rather than baseline-aligned. Words are
      // naturally much wider than tall, so the same paddingScale reserve
      // that's added to width is added to height too (via chipReserve),
      // otherwise 'spread' reads as almost entirely horizontal.
      ctx.fillText = (text, x) => {
        const fontSize = currentFontSize();
        const h = fontSize * 1.32 + chipReserve(fontSize, paddingScale) * 2;
        ctx.fillRect(x, -h / 2, ctx.measureText(text).width, h);
      };
      ctx.strokeText = () => { };
    }
    return ctx;
  };
  return canvasEl;
};

// Non-chip styles use d3-cloud's real glyph-shaped collision so bare text
// interlocks tightly, but glyphs draw at their text baseline while the
// rendered DOM word is flex-centered - shift each string by half its
// ascent/descent gap so the collision sprite is centered the same way.
export const glyphSpriteCanvas = () => {
  const canvasEl = document.createElement('canvas');
  const originalGetContext = canvasEl.getContext.bind(canvasEl);
  canvasEl.getContext = (type, opts) => {
    const ctx = originalGetContext(type, opts);
    if (type === '2d' && ctx && !ctx.__glyphPatched) {
      ctx.__glyphPatched = true;
      const originalFillText = ctx.fillText.bind(ctx);
      const originalStrokeText = ctx.strokeText.bind(ctx);
      const centerShift = (text) => {
        const m = ctx.measureText(text);
        if (Number.isFinite(m.actualBoundingBoxAscent) && Number.isFinite(m.actualBoundingBoxDescent)) {
          return (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
        }
        return 0;
      };
      ctx.fillText = (text, x, y, maxWidth) => originalFillText(text, x, y + centerShift(text), maxWidth);
      ctx.strokeText = (text, x, y, maxWidth) => originalStrokeText(text, x, y + centerShift(text), maxWidth);
    }
    return ctx;
  };
  return canvasEl;
};
